import { useEffect, useRef, useState } from "react";
import { PlatformApiError, type PlatformApiClient } from "../../api";
import { CampaignSchedulePanel } from "./CampaignSchedulePanel";
import { CampaignDispatchReadinessPanel } from "./CampaignDispatchReadinessPanel";

interface CampaignLocation {
  id: string;
  name: string;
  ready: boolean;
}
interface LocationResponse {
  locations: CampaignLocation[];
  sendAuthorized: false;
}
interface CampaignListResponse { campaigns: CampaignRecord[]; sendAuthorized: false; }
interface CampaignRecord {
  campaignId: string;
  channel: "email" | "whatsapp";
  locationId: string;
  state: "draft" | "approved" | "paused";
  approved: boolean;
  approvalRevision: number;
  sendAuthorized: false;
  replayed?: boolean;
}
type LoadingState =
  | { kind: "loading" }
  | { kind: "ready"; locations: CampaignLocation[] }
  | { kind: "disabled" }
  | { kind: "forbidden" }
  | { kind: "error" };

const CAMPAIGN_ID = {
  email: /^rac_campaign_[a-f0-9-]{36}$/,
  whatsapp: /^rac_wa_[a-f0-9-]{36}$/,
} as const;
const CAMPAIGN_PATH = {
  email: "/acquisition/campaigns",
  whatsapp: "/acquisition/whatsapp/campaigns",
} as const;
const LOCATION_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/;

function isCampaign(value: CampaignRecord, channel: "email" | "whatsapp"): boolean {
  return !!value &&
    value.channel === channel &&
    CAMPAIGN_ID[channel].test(value.campaignId) &&
    LOCATION_ID.test(value.locationId) &&
    ["draft", "approved", "paused"].includes(value.state) &&
    typeof value.approved === "boolean" &&
    Number.isSafeInteger(value.approvalRevision) &&
    value.approvalRevision >= 0 &&
    value.sendAuthorized === false &&
    value.approved === (value.state === "approved");
}

function campaignMessage(cause: unknown, onExpired: () => void): string {
  if (cause instanceof PlatformApiError) {
    if (cause.status === 401) {
      onExpired();
      return "La sesión terminó. Volvé a ingresar.";
    }
    if (cause.status === 403) return "Tu usuario no tiene permiso para administrar campañas.";
    if (cause.status === 404) return "La administración de campañas aún no está disponible.";
    if (cause.status === 409) return "La campaña cambió de estado. Actualizá antes de continuar.";
    if (cause.status === 413) return "El servidor rechazó una solicitud demasiado grande.";
  }
  return "No se pudo confirmar la operación. Verificá el estado antes de intentar nuevamente.";
}

/** Uses the existing Platform cookie session, CSRF and Reputation route allowlist. */
export function CampaignManagementView({
  slug, api, onExpired,
}: {
  slug: string;
  api: PlatformApiClient;
  onExpired: () => void;
}) {
  const [channel, setChannel] = useState<"email" | "whatsapp">("email");
  const basePath = CAMPAIGN_PATH[channel];
  const [availability, setAvailability] = useState<LoadingState>({ kind: "loading" });
  const [locationId, setLocationId] = useState("");
  const [campaign, setCampaign] = useState<CampaignRecord | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [confirmApproval, setConfirmApproval] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const requestKey = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setAvailability({ kind: "loading" });
    setCampaign(null);
    setCampaigns([]);
    setLocationId("");
    requestKey.current = null;
    void api.reputation<LocationResponse>(slug, basePath + "/locations")
      .then(async result => {
        if (!active) return;
        if (result.sendAuthorized !== false || !Array.isArray(result.locations) ||
          result.locations.length > 100 ||
          !result.locations.every(loc => loc && LOCATION_ID.test(loc.id) &&
            typeof loc.name === "string" && loc.name.length <= 200 &&
            typeof loc.ready === "boolean")) {
          setAvailability({ kind: "error" });
          return;
        }
        // Server is the authority: recover saved drafts/approvals after refresh.
        // No browser persistence, recipient lists or browser-side campaign authority.
        const saved = await api.reputation<CampaignListResponse>(slug, basePath);
        if (!active) return;
        if (saved.sendAuthorized !== false || !Array.isArray(saved.campaigns) ||
          saved.campaigns.length > 20 || !saved.campaigns.every(item => isCampaign(item, channel))) {
          setAvailability({ kind: "error" });
          return;
        }
        setCampaigns(saved.campaigns);
        setAvailability({ kind: "ready", locations: result.locations });
      })
      .catch(cause => {
        if (!active) return;
        if (cause instanceof PlatformApiError) {
          if (cause.status === 401) { onExpired(); return; }
          if (cause.status === 404) { setAvailability({ kind: "disabled" }); return; }
          if (cause.status === 403) { setAvailability({ kind: "forbidden" }); return; }
        }
        setAvailability({ kind: "error" });
      });
    return () => { active = false; };
  }, [slug, api, onExpired, channel, basePath]);

  const locations = availability.kind === "ready" ? availability.locations : [];
  const selected = locations.find(loc => loc.id === locationId);
  const canCreate = availability.kind === "ready" && selected?.ready === true &&
    !busy && campaign === null;

  function chooseLocation(id: string) {
    setLocationId(id);
    requestKey.current = null;
    setMessage("");
  }

  async function prepare() {
    if (!canCreate || !LOCATION_ID.test(locationId)) return;
    setBusy(true);
    setMessage("");
    requestKey.current ??= "campaign-web-" + globalThis.crypto.randomUUID();
    try {
      const next = await api.reputation<CampaignRecord>(slug, basePath, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ requestKey: requestKey.current, locationId }),
      });
      if (!isCampaign(next, channel) || next.locationId !== locationId || next.state !== "draft") {
        throw new Error("campaign_contract_invalid");
      }
      setCampaign(next);
      setCampaigns(previous => [next, ...previous].slice(0, 20));
      requestKey.current = null;
      setMessage("Borrador registrado. No se seleccionaron destinatarios ni se enviaron invitaciones.");
    } catch (cause) {
      setMessage(campaignMessage(cause, onExpired));
    } finally {
      setBusy(false);
    }
  }

  async function action(operation: "approve" | "pause" | "refresh") {
    if (!campaign || busy || (operation === "approve" && !confirmApproval)) return;
    setBusy(true);
    setMessage("");
    try {
      const path = basePath + "/" + encodeURIComponent(campaign.campaignId);
      const next = await api.reputation<CampaignRecord>(
        slug,
        path + (operation === "refresh" ? "" : "/" + operation),
        operation === "refresh" ? {} : {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: "{}",
        },
      );
      if (!isCampaign(next, channel) || next.campaignId !== campaign.campaignId ||
        next.locationId !== campaign.locationId) {
        throw new Error("campaign_contract_invalid");
      }
      setCampaign(next);
      setCampaigns(previous => previous.map(item => item.campaignId === next.campaignId ? next : item));
      setConfirmApproval(false);
      setMessage(operation === "approve"
        ? "Campaña aprobada para preparación. Esto NO envía invitaciones."
        : operation === "pause"
          ? "Campaña pausada. No puede autorizar nuevos intentos."
          : "Estado actualizado desde el servidor.");
    } catch (cause) {
      setMessage(campaignMessage(cause, onExpired));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rep-acquisition-panel rep-acquisition-campaign"
      aria-labelledby="rep-acquisition-campaign-title">
      <div className="rep-acquisition-campaign-heading">
        <div>
          <h2 id="rep-acquisition-campaign-title">Preparar campaña</h2>
          <p>Elegí un local y prepará un borrador. La aprobación es independiente de cualquier envío.</p>
        </div>
        <span className="rep-acquisition-mode">Solo borradores</span>
      </div>

      <div className="rep-acquisition-capability" role="status" aria-live="polite">
        <strong>{availability.kind === "ready"
          ? "Podés preparar borradores en este canal"
          : availability.kind === "loading"
            ? "Comprobando disponibilidad del canal…"
            : "La preparación no está disponible en este canal"}</strong>
        <p>Guardar o aprobar un borrador nunca envía invitaciones. La disponibilidad se verifica por canal y el envío real requiere autorización independiente.</p>
      </div>

      <label htmlFor="rep-campaign-channel">Canal de campaña</label>
      <select id="rep-campaign-channel" value={channel} disabled={busy}
        onChange={event => {
          const selected = event.target.value;
          if (selected === "email" || selected === "whatsapp") {
            setChannel(selected);
            setMessage("");
            setConfirmApproval(false);
          }
        }}>
        <option value="email">Email</option>
        <option value="whatsapp">WhatsApp Business</option>
      </select>
      {channel === "whatsapp" &&
        <p>Las plantillas WhatsApp requieren aprobación de Meta y autorización del negocio. Preparar una campaña no envía mensajes.</p>}
      {availability.kind === "loading" && <p role="status">Consultando los locales disponibles…</p>}
      {availability.kind === "disabled" &&
        <p role="status">Las campañas todavía no están habilitadas para este negocio. No se guardó ni envió nada.</p>}
      {availability.kind === "forbidden" &&
        <p role="status">Solo un propietario o gerente autorizado puede administrar campañas.</p>}
      {availability.kind === "error" &&
        <p role="alert">No se pudieron consultar los locales. No se realizó ninguna operación.</p>}

      {availability.kind === "ready" && <>
        <div className="rep-acquisition-saved" aria-labelledby="rep-acquisition-saved-title">
          <h3 id="rep-acquisition-saved-title">Campañas guardadas</h3>
          {campaigns.length === 0 && <p>Todavía no hay campañas guardadas.</p>}
          {campaigns.map(item =>
            <div key={item.campaignId} className="rep-acquisition-saved-item">
              <span>{item.channel === "whatsapp" ? "WhatsApp" : "Email"} · {locations.find(loc => loc.id === item.locationId)?.name ?? "Local registrado"} · {
                item.state === "draft" ? "Borrador" :
                  item.state === "approved" ? "Aprobada" : "Pausada"
              }</span>
              <button type="button" disabled={busy} aria-label="Abrir campaña guardada"
                onClick={() => {
                  setCampaign(item);
                  setLocationId(item.locationId);
                  setConfirmApproval(false);
                  setMessage("");
                  requestKey.current = null;
                }}>Abrir</button>
            </div>)}
          {campaign !== null &&
            <button type="button" disabled={busy} onClick={() => {
              setCampaign(null);
              setLocationId("");
              setConfirmApproval(false);
              setMessage("");
              requestKey.current = null;
            }}>Preparar otra campaña</button>}
        </div>
        <label htmlFor="rep-campaign-location">¿En qué local querés recibir las reseñas?</label>
        <select id="rep-campaign-location" value={locationId} disabled={busy || campaign !== null}
          onChange={event => chooseLocation(event.target.value)}>
          <option value="">Elegí un local</option>
          {locations.map(loc =>
            <option key={loc.id} value={loc.id} disabled={!loc.ready}>
              {loc.name} {loc.ready ? "" : "— Falta configurar el enlace de Google o habilitar el negocio"}
            </option>)}
        </select>
        {locations.length === 0 && <p>No hay locales configurados todavía.</p>}
        {selected?.ready === false &&
          <p>Este local todavía no cumple los requisitos para preparar una campaña.</p>}

        {campaign === null ?
          <div className="rep-acquisition-actions">
            <button type="button" className="primary-button" disabled={!canCreate}
              onClick={() => { void prepare(); }}>
              {busy ? "Guardando…" : "Crear borrador"}
            </button>
          </div> :
          <div className="rep-acquisition-campaign-state">
            <h3>Estado de la campaña</h3>
            <p><strong>{campaign.state === "draft" ? "Borrador" :
              campaign.state === "approved" ? "Aprobada para preparación" : "Pausada"}</strong></p>
            <p>Canal: {campaign.channel === "whatsapp" ? "WhatsApp" : "Email"}</p>
            <p>Local: {locations.find(loc => loc.id === campaign.locationId)?.name ?? "Local registrado"}</p>
            <p>Revisión de aprobación: {campaign.approvalRevision}</p>
            {campaign.state !== "approved" &&
              <label className="rep-acquisition-check">
                <input type="checkbox" checked={confirmApproval} disabled={busy}
                  onChange={e => setConfirmApproval(e.target.checked)} />
                Confirmo que quiero aprobar este borrador. Sé que aprobar no envía invitaciones.
              </label>}
            <div className="rep-acquisition-actions">
              {campaign.state !== "approved" &&
                <button className="primary-button" type="button"
                  disabled={busy || !confirmApproval}
                  onClick={() => { void action("approve"); }}>
                  Aprobar borrador
                </button>}
              {campaign.state === "approved" &&
                <button type="button" disabled={busy} onClick={() => { void action("pause"); }}>
                  Pausar campaña
                </button>}
              <button type="button" disabled={busy}
                onClick={() => { void action("refresh"); }}>Actualizar estado</button>
            </div>
            {campaign.state === "approved" &&
              <CampaignSchedulePanel key={campaign.campaignId}
                slug={slug} api={api} campaignId={campaign.campaignId}
                channel={channel} onExpired={onExpired} />}
          </div>}
      </>}
      <CampaignDispatchReadinessPanel slug={slug} api={api} onExpired={onExpired} compact />
      {message && <p role="status" aria-live="polite" className="rep-acquisition-operation-message">{message}</p>}
      <p className="rep-acquisition-no-send">
        No se importan destinatarios, no se generan invitaciones y no se envían mensajes desde esta pantalla.
      </p>
    </section>
  );
}
